/**
 * Types for the Deckorate "Create slides" wizard. Generation is abstracted
 * behind SlideGenerator, implemented by the real backend client
 * (apiGenerator + slideGenerationApi).
 */

/** Step 1 — where the slides are sourced from. */
export type SlideSource = "text" | "upload";

/** Step 3 — design mode. "freehand" = AI design (endpoint ready); "templates" = placeholder (backend WIP). */
export type DesignMode = "freehand" | "templates";

export type Tone =
  | "Professional"
  | "Conversational"
  | "Persuasive"
  | "Inspirational"
  | "Technical"
  | "Friendly";

export const TONES: readonly Tone[] = [
  "Professional",
  "Conversational",
  "Persuasive",
  "Inspirational",
  "Technical",
  "Friendly",
];

/**
 * A document the user attached on step 2. `chars` drives the "· 7.6k chars"
 * chip; `file` is the real File sent to the backend `/upload` endpoint for
 * server-side text extraction. The File is only ever used inside the dialog
 * (where generation runs) — it never crosses the messageParent boundary.
 */
export interface UploadedDoc {
  name: string;
  chars: number;
  file?: File;
}

/**
 * A finished slide handed from the dialog back to the task pane for insertion.
 * `points`/`title` render the placeholder slide today; `xml` (the backend's
 * `<p:spTree>`) is carried for when real OOXML rendering lands.
 */
export interface GeneratedSlide {
  index: number;
  title: string;
  points: string[];
  /** Slide archetype (mapped from the backend intent_tag) — drives the preview layout. */
  category?: SlideCategory;
  /** Directly-insertable base64 `.pptx` (preferred render path). */
  pptxBase64?: string;
  xml?: string | null;
}

/** Slide archetype (mapped from the backend IntentTag); drives preview layout. */
export type SlideCategory =
  | "COVER"
  | "OVERVIEW"
  | "PROCESS"
  | "COMPARISON"
  | "STATISTICAL"
  | "HIERARCHICAL"
  | "TIMELINE"
  | "CLOSING";

/** Everything the wizard collects, handed to the generator on "Generate". */
export interface CreateSlideConfig {
  source: SlideSource;
  topic: string;
  documents: UploadedDoc[];
  audience: string;
  slideCount: number;
  tone: Tone;
  coverSlide: boolean;
  closingSlide: boolean;
  /** Step 3 choice. "freehand" → the ready create-slide API; "templates" → placeholder. */
  designMode: DesignMode;
  /**
   * Blob storage location of the uploaded document, set once the file has been
   * uploaded to blob (mechanism TBD with backend). When present, generation
   * uses input_type="document"; otherwise it uses the topic.
   */
  blobContainer?: string;
  blobName?: string;
}

export const DEFAULT_CONFIG: CreateSlideConfig = {
  source: "text",
  topic: "",
  documents: [],
  audience: "",
  slideCount: 6,
  tone: "Professional",
  coverSlide: true,
  closingSlide: true,
  designMode: "freehand",
};

export const SLIDE_COUNT_MIN = 3;
export const SLIDE_COUNT_MAX = 20;

/** Per-slide lifecycle shown in the generation progress list. */
export type SlideGenStatus =
  | "waiting"
  | "picking"
  | "generating"
  | "done"
  | "error";

export interface SlidePlan {
  /** 1-based position in the deck. */
  index: number;
  title: string;
  status: SlideGenStatus;
  /** Sub-line, e.g. "Sketching the outline". */
  detail?: string;
}

/** Overall generation phase driving the progress view. */
export type GenerationPhase =
  | "idle"
  | "outlining"
  | "generating"
  | "done"
  | "cancelled"
  | "error";

/** Callbacks a generator emits as it works. */
export interface GeneratorCallbacks {
  onPlan: (plans: SlidePlan[]) => void;
  onSlideUpdate: (index: number, status: SlideGenStatus, detail?: string) => void;
  onProgress: (current: number, total: number) => void;
  onPhase: (phase: GenerationPhase) => void;
  onError: (message: string) => void;
}

/** Handle returned by a generation run so the caller can cancel it. */
export interface GenerationRun {
  cancel: () => void;
}

/**
 * Pluggable slide generator. Implemented by the backend client (apiGenerator),
 * which streams from POST /v1/deckorate/slides/create-slide-ai and drives the progress UI.
 */
export interface SlideGenerator {
  generate(config: CreateSlideConfig, callbacks: GeneratorCallbacks): GenerationRun;
}
